/**
 * @module features/files/file-utils
 * @description Utilities for file type detection, content type handling, and file icons
 */

// Content type dictionary - maps MIME types to user-friendly names
const contentTypeDisplayNames = {
    // Images
    'image/jpeg': 'JPEG Image',
    'image/jpg': 'JPG Image',
    'image/png': 'PNG Image',
    'image/gif': 'GIF Image',
    'image/svg+xml': 'SVG Image',
    'image/bmp': 'Bitmap Image',
    'image/webp': 'WebP Image',
    'image/tiff': 'TIFF Image',

    // Documents
    'application/pdf': 'PDF Document',
    'application/msword': 'Microsoft Word Document',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Microsoft Word Document',
    'application/vnd.ms-excel': 'Microsoft Excel Spreadsheet',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Microsoft Excel Spreadsheet',
    'application/vnd.ms-powerpoint': 'Microsoft PowerPoint Presentation',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'Microsoft PowerPoint Presentation',

    // Text files
    'text/plain': 'Text File',
    'text/html': 'HTML File',
    'text/css': 'CSS File',
    'text/javascript': 'JavaScript File',
    'text/csv': 'CSV File',
    'text/xml': 'XML File',
    'application/json': 'JSON File',
    'application/xml': 'XML File',

    // Archives
    'application/zip': 'ZIP Archive',
    'application/x-zip-compressed': 'ZIP Archive',
    'application/x-rar-compressed': 'RAR Archive',
    'application/x-7z-compressed': '7-Zip Archive',
    'application/gzip': 'GZIP Archive',

    // Audio
    'audio/mpeg': 'MP3 Audio',
    'audio/mp3': 'MP3 Audio',
    'audio/wav': 'WAV Audio',
    'audio/ogg': 'OGG Audio',
    'audio/webm': 'WebM Audio',

    // Video
    'video/mp4': 'MP4 Video',
    'video/mpeg': 'MPEG Video',
    'video/quicktime': 'QuickTime Video',
    'video/x-msvideo': 'AVI Video',
    'video/webm': 'WebM Video',

    // Other
    'application/octet-stream': 'Binary File'
};

/**
 * Get file icon emoji based on file extension
 * @param {string} filename - The filename to get icon for
 * @returns {string} Emoji icon representing the file type
 */
export function getFileIcon(filename) {
    const ext = filename.split('.').pop().toLowerCase();

    const iconMap = {
        pdf: '📄',
        doc: '📝',
        docx: '📝',
        xls: '📊',
        xlsx: '📊',
        ppt: '📊',
        pptx: '📊',
        jpg: '🖼️',
        jpeg: '🖼️',
        png: '🖼️',
        gif: '🖼️',
        svg: '🖼️',
        mp4: '🎥',
        avi: '🎥',
        mov: '🎥',
        mp3: '🎵',
        wav: '🎵',
        zip: '📦',
        rar: '📦',
        txt: '📃',
        csv: '📊'
    };

    return iconMap[ext] || '📄';
}

/**
 * Determine if a file can be viewed in the browser
 * @param {string} contentType - MIME type of the file
 * @returns {boolean} True if file can be viewed in browser
 */
export function canViewInBrowser(contentType) {
    if (!contentType) return false;

    const viewableTypes = [
        // Images
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/svg+xml',
        'image/bmp',
        'image/webp',

        // Documents
        'application/pdf',

        // Text files
        'text/plain',
        'text/html',
        'text/css',
        'text/javascript',
        'text/csv',
        'text/xml',
        'application/json',
        'application/xml',

        // Audio
        'audio/mpeg',
        'audio/mp3',
        'audio/wav',
        'audio/ogg',
        'audio/webm',

        // Video
        'video/mp4',
        'video/webm',
        'video/ogg'
    ];

    // Check if the content type matches any viewable type
    return viewableTypes.some(type => contentType.toLowerCase().startsWith(type.toLowerCase()));
}

/**
 * Get user-friendly name for a content type
 * @param {string} contentType - MIME type string
 * @returns {string} Human-readable content type name
 */
export function getContentTypeName(contentType) {
    if (!contentType) return 'Unknown File Type';

    // Try exact match first
    if (contentTypeDisplayNames[contentType.toLowerCase()]) {
        return contentTypeDisplayNames[contentType.toLowerCase()];
    }

    // Try partial match (for content types with charset, etc.)
    const baseType = contentType.split(';')[0].trim().toLowerCase();
    if (contentTypeDisplayNames[baseType]) {
        return contentTypeDisplayNames[baseType];
    }

    // Return the base type as fallback
    return `${baseType.split('/')[1]?.toUpperCase()  } File` || 'Unknown File Type';
}

/**
 * Infer content type from file extension
 * @param {string} filename - The filename to infer type from
 * @returns {string|null} Inferred MIME type or null if cannot be determined
 */
export function inferContentTypeFromFilename(filename) {
    if (!filename) return null;

    // Check if filename actually has an extension (contains a dot and the last part looks like an extension)
    const parts = filename.split('.');
    if (parts.length < 2) {
        return null; // No extension
    }

    const ext = parts[parts.length - 1].toLowerCase().trim();

    // Check if the "extension" is suspiciously long (probably not a real extension)
    if (ext.length > 5 || ext.includes(' ')) {
        return null;
    }

    const extensionMap = {
        // Images
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'png': 'image/png',
        'gif': 'image/gif',
        'svg': 'image/svg+xml',
        'bmp': 'image/bmp',
        'webp': 'image/webp',
        'tiff': 'image/tiff',
        'tif': 'image/tiff',

        // Documents
        'pdf': 'application/pdf',
        'doc': 'application/msword',
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'xls': 'application/vnd.ms-excel',
        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'ppt': 'application/vnd.ms-powerpoint',
        'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',

        // Text
        'txt': 'text/plain',
        'html': 'text/html',
        'htm': 'text/html',
        'css': 'text/css',
        'js': 'text/javascript',
        'csv': 'text/csv',
        'xml': 'text/xml',
        'json': 'application/json',

        // Archives
        'zip': 'application/zip',
        'rar': 'application/x-rar-compressed',
        '7z': 'application/x-7z-compressed',
        'gz': 'application/gzip',

        // Audio
        'mp3': 'audio/mpeg',
        'wav': 'audio/wav',
        'ogg': 'audio/ogg',

        // Video
        'mp4': 'video/mp4',
        'avi': 'video/x-msvideo',
        'mov': 'video/quicktime',
        'webm': 'video/webm',
        'mpeg': 'video/mpeg',
        'mpg': 'video/mpeg'
    };

    return extensionMap[ext] || null;
}

/**
 * Get user-friendly explanation for why a file can't be viewed in browser
 * @param {string} contentType - MIME type of the file
 * @returns {string} Explanation message
 */
export function getNotViewableReason(contentType) {
    if (!contentType || contentType === 'application/octet-stream') {
        return 'Unknown file type - browsers cannot display this format';
    }

    const type = contentType.toLowerCase();

    // Microsoft Office documents
    if (type.includes('word') || type.includes('msword') || type.includes('document')) {
        return 'Word documents require Microsoft Word or compatible software';
    }
    if (type.includes('excel') || type.includes('spreadsheet')) {
        return 'Excel spreadsheets require Microsoft Excel or compatible software';
    }
    if (type.includes('powerpoint') || type.includes('presentation')) {
        return 'PowerPoint files require Microsoft PowerPoint or compatible software';
    }

    // Archives
    if (type.includes('zip') || type.includes('rar') || type.includes('7z') || type.includes('gzip')) {
        return 'Archive files must be extracted before viewing their contents';
    }

    // Generic fallback
    return 'This file type requires specific software to open';
}

// Default export
export default {
    getFileIcon,
    canViewInBrowser,
    getContentTypeName,
    inferContentTypeFromFilename,
    getNotViewableReason
};
